import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Eye, GitBranch, CheckCircle2, XCircle } from "lucide-react";
import { useTenantScope, PageHeader } from "./shared";

export default function FieldMappingStudio() {
  const { id, target } = useParams();
  const [sp] = useSearchParams();
  const urlTenant = sp.get("tenantId") || "";
  const scope = useTenantScope();
  const tenantId = scope.isSuper ? (scope.tenantId || urlTenant) : "";
  const params = tenantId ? { tenantId } : {};
  const suffix = tenantId ? `?tenantId=${tenantId}` : "";

  const [meta, setMeta] = useState(null);     // canonical target meta {fields, defaultDedupeKey}
  const [transforms, setTransforms] = useState(["none"]);
  const [columns, setColumns] = useState([]); // source columns
  const [rows, setRows] = useState([]);       // [{target, source, transform, required, default}]
  const [dedupeKey, setDedupeKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { if (scope.isSuper && urlTenant && !scope.tenantId) scope.setTenantId(urlTenant); }, [urlTenant]);

  const load = useCallback(async () => {
    if (scope.isSuper && !tenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [conns, sample, maps] = await Promise.all([
        integrationService.connectors(),
        integrationService.sample(id, params),
        integrationService.mappings(id, params),
      ]);
      const tmeta = conns.canonicalTargets[target];
      setMeta(tmeta);
      setTransforms(conns.transforms || ["none"]);
      setColumns(sample.columns || []);
      setDedupeKey(tmeta?.defaultDedupeKey || "");
      const existing = (maps.items || []).find((m) => m.targetEntity === target);
      const byField = {};
      (existing?.fields || []).forEach((f) => { byField[f.target] = f; });
      if (existing?.dedupeKey) setDedupeKey(existing.dedupeKey);
      setRows((tmeta?.fields || []).map((f) => ({
        target: f,
        source: byField[f]?.source || "",
        transform: byField[f]?.transform || "none",
        required: byField[f]?.required || false,
        default: byField[f]?.default || "",
      })));
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id, target, tenantId]);
  useEffect(() => { load(); }, [load]);

  const update = (i, key, val) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const mappedFields = () => rows.filter((r) => r.source || r.default).map((r) => ({
    source: r.source || "", target: r.target, transform: r.transform,
    required: r.required, default: r.default || undefined,
  }));

  const doPreview = async () => {
    try {
      const r = await integrationService.previewMapping(id, { targetEntity: target, dedupeKey, fields: mappedFields() }, params);
      setPreview(r.preview || []);
      if (!(r.preview || []).length) toast.info("No sample rows to preview. Upload a file or use a sandbox connector.");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await integrationService.saveMapping(id, target, { dedupeKey, fields: mappedFields() }, params);
      toast.success("Mapping saved");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="mapping-studio-page">
      <PageHeader icon={GitBranch} title="Field Mapping Studio" subtitle={`Map source fields → ${target}`} />
      <Link to={`/admin/integrations/${id}${suffix}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="mapping-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to integration
      </Link>

      {scope.isSuper && !tenantId ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center"><p className="text-sm text-muted-foreground">Open this from the integration page so a tenant is in scope.</p></div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !meta ? (
        <p className="text-sm text-muted-foreground">Unsupported target.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap bg-card border border-border rounded-xl p-4">
            <label className="text-xs text-muted-foreground">Dedupe key (idempotent upsert)</label>
            <select value={dedupeKey} onChange={(e) => setDedupeKey(e.target.value)} className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" data-testid="dedupe-key">
              {meta.fields.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <span className="text-[11px] text-muted-foreground">{columns.length} source column(s) available</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={doPreview} className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted" data-testid="mapping-preview"><Eye className="h-3.5 w-3.5" /> Preview</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="mapping-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save mapping</button>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="mapping-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Canonical field</th>
                  <th className="px-4 py-2">Source column</th>
                  <th className="px-4 py-2">Transform</th>
                  <th className="px-4 py-2">Default</th>
                  <th className="px-4 py-2 text-center">Required</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.target} className="border-b border-border last:border-0" data-testid={`map-row-${r.target}`}>
                    <td className="px-4 py-2 font-medium text-foreground">{r.target}</td>
                    <td className="px-4 py-2">
                      <select value={r.source} onChange={(e) => update(i, "source", e.target.value)} className="bg-background border border-border rounded-lg px-2 py-1 text-xs text-foreground w-full" data-testid={`map-source-${r.target}`}>
                        <option value="">— none —</option>
                        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select value={r.transform} onChange={(e) => update(i, "transform", e.target.value)} className="bg-background border border-border rounded-lg px-2 py-1 text-xs text-foreground" data-testid={`map-transform-${r.target}`}>
                        {transforms.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <input value={r.default} onChange={(e) => update(i, "default", e.target.value)} placeholder="—" className="bg-background border border-border rounded-lg px-2 py-1 text-xs text-foreground w-24" data-testid={`map-default-${r.target}`} />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={r.required} onChange={(e) => update(i, "required", e.target.checked)} className="h-4 w-4 accent-primary" data-testid={`map-required-${r.target}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview && (
            <div className="bg-card border border-border rounded-xl p-5" data-testid="preview-panel">
              <h2 className="text-sm font-bold text-foreground mb-3">Preview ({preview.length} sample row(s))</h2>
              {preview.length === 0 ? <p className="text-xs text-muted-foreground">No sample rows. Upload data or use a sandbox connector.</p> : (
                <div className="space-y-2">
                  {preview.map((p, i) => (
                    <div key={i} className="border border-border rounded-lg p-3" data-testid={`preview-row-${i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {p.valid ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                        <span className={`text-xs font-semibold ${p.valid ? "text-emerald-600" : "text-red-600"}`}>{p.valid ? "Valid → will sync" : "Invalid → routed to Error Log"}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Canonical: {JSON.stringify(p.canonical)}</p>
                      {!p.valid && <p className="text-[11px] text-red-600">{(p.errors || []).join("; ")}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
